import React, { useState, useEffect } from 'react';
import {
  CloudIcon,
  EnvelopeIcon,
  ServerIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import ConfigSelector from './ConfigSelector';
import { backupConfigAPI } from '../services/api';

const ModularBackupConfig = ({ serverId = 'local' }) => {
  const [hybridConfig, setHybridConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedConfigs, setSelectedConfigs] = useState({
    awsRegion: null,
    awsBucket: null,
    awsAccessKey: null,
    awsSecretKey: null,
    emailFrom: null,
    emailTo: null,
    smtpHost: null,
    smtpPort: null,
    smtpUser: null,
    smtpPassword: null,
    pgHost: null,
    pgPort: null,
    pgUser: null,
    pgPassword: null
  });

  useEffect(() => {
    fetchHybridConfig();
  }, [serverId]);

  const fetchHybridConfig = async () => {
    try {
      const response = await backupConfigAPI.getHybrid(serverId);
      setHybridConfig(response.data);
      
      // Inicializar seleções com base na configuração híbrida
      const configs = response.data;
      const sources = configs._configSources;
      
      // Mapear cada campo para sua fonte
      const initialSelections = {};
      
      // AWS
      if (sources.aws.aws_region?.value) {
        initialSelections.awsRegion = { type: 'global', key: 'aws_region', value: sources.aws.aws_region.value, source: 'global_config' };
      }
      if (sources.aws.aws_bucket?.value) {
        initialSelections.awsBucket = { type: 'global', key: 'aws_bucket', value: sources.aws.aws_bucket.value, source: 'global_config' };
      }
      if (sources.aws.aws_access_key_id?.value) {
        initialSelections.awsAccessKey = { type: 'global', key: 'aws_access_key_id', value: sources.aws.aws_access_key_id.value, source: 'global_config' };
      }
      if (sources.aws.aws_secret_access_key?.value) {
        initialSelections.awsSecretKey = { type: 'global', key: 'aws_secret_access_key', value: sources.aws.aws_secret_access_key.value, source: 'global_config' };
      }

      // Email
      if (sources.email.email_from?.value) {
        initialSelections.emailFrom = { type: 'global', key: 'email_from', value: sources.email.email_from.value, source: 'global_config' };
      }
      if (sources.email.email_to?.value) {
        initialSelections.emailTo = { type: 'global', key: 'email_to', value: sources.email.email_to.value, source: 'global_config' };
      }
      if (sources.email.smtp_host?.value) {
        initialSelections.smtpHost = { type: 'global', key: 'smtp_host', value: sources.email.smtp_host.value, source: 'global_config' };
      }
      if (sources.email.smtp_port?.value) {
        initialSelections.smtpPort = { type: 'global', key: 'smtp_port', value: sources.email.smtp_port.value, source: 'global_config' };
      }
      if (sources.email.smtp_user?.value) {
        initialSelections.smtpUser = { type: 'global', key: 'smtp_user', value: sources.email.smtp_user.value, source: 'global_config' };
      }
      if (sources.email.smtp_password?.value) {
        initialSelections.smtpPassword = { type: 'global', key: 'smtp_password', value: sources.email.smtp_password.value, source: 'global_config' };
      }

      // Database
      if (sources.database.db_host?.value) {
        initialSelections.pgHost = { type: 'global', key: 'db_host', value: sources.database.db_host.value, source: 'global_config' };
      }
      if (sources.database.db_port?.value) {
        initialSelections.pgPort = { type: 'global', key: 'db_port', value: sources.database.db_port.value, source: 'global_config' };
      }
      if (sources.database.db_user?.value) {
        initialSelections.pgUser = { type: 'global', key: 'db_user', value: sources.database.db_user.value, source: 'global_config' };
      }
      if (sources.database.db_password?.value) {
        initialSelections.pgPassword = { type: 'global', key: 'db_password', value: sources.database.db_password.value, source: 'global_config' };
      }

      setSelectedConfigs(initialSelections);
    } catch (error) {
      console.error('Erro ao buscar configuração híbrida:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (field, config) => {
    setSelectedConfigs(prev => ({
      ...prev,
      [field]: config
    }));
  };

  const getConfigSummary = () => {
    const globalCount = Object.values(selectedConfigs).filter(config => config?.type === 'global').length;
    const customCount = Object.values(selectedConfigs).filter(config => config?.type === 'custom').length;
    const total = Object.values(selectedConfigs).filter(config => config).length;
    
    return { globalCount, customCount, total };
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const summary = getConfigSummary();

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <InformationCircleIcon className="h-6 w-6 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-blue-900">Configurações Modulares</h3>
            <p className="mt-1 text-blue-700">
              Escolha entre usar configurações globais centralizadas ou valores personalizados para cada campo.
              Configurações globais podem ser reutilizadas em multiple backups e são gerenciadas centralmente.
            </p>
            <div className="mt-2 flex items-center space-x-4 text-sm">
              <span className="text-blue-600">
                <span className="font-medium">{summary.globalCount}</span> configurações globais
              </span>
              <span className="text-blue-600">
                <span className="font-medium">{summary.customCount}</span> valores personalizados
              </span>
              <span className="text-blue-600">
                <span className="font-medium">{summary.total}</span> de 14 campos configurados
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Configurações AWS */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <CloudIcon className="h-6 w-6 text-orange-600 mr-3" />
            <h3 className="text-lg font-medium text-gray-900">Amazon Web Services</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ConfigSelector
              category="aws"
              selectedConfig={selectedConfigs.awsRegion}
              onConfigSelect={(config) => handleConfigChange('awsRegion', config)}
              label="Região AWS"
              description="Região onde os recursos AWS serão criados"
              required
            />
            <ConfigSelector
              category="aws"
              selectedConfig={selectedConfigs.awsBucket}
              onConfigSelect={(config) => handleConfigChange('awsBucket', config)}
              label="Bucket S3"
              description="Nome do bucket para armazenar backups"
              required
            />
            <ConfigSelector
              category="aws"
              selectedConfig={selectedConfigs.awsAccessKey}
              onConfigSelect={(config) => handleConfigChange('awsAccessKey', config)}
              label="Access Key ID"
              description="Chave de acesso da AWS"
              required
            />
            <ConfigSelector
              category="aws"
              selectedConfig={selectedConfigs.awsSecretKey}
              onConfigSelect={(config) => handleConfigChange('awsSecretKey', config)}
              label="Secret Access Key"
              description="Chave secreta da AWS"
              required
            />
          </div>
        </div>
      </div>

      {/* Configurações de Email */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <EnvelopeIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h3 className="text-lg font-medium text-gray-900">Notificações por Email</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ConfigSelector
              category="email"
              selectedConfig={selectedConfigs.emailFrom}
              onConfigSelect={(config) => handleConfigChange('emailFrom', config)}
              label="Email Remetente"
              description="Endereço que enviará as notificações"
            />
            <ConfigSelector
              category="email"
              selectedConfig={selectedConfigs.emailTo}
              onConfigSelect={(config) => handleConfigChange('emailTo', config)}
              label="Email Destinatário"
              description="Endereço que receberá as notificações"
            />
            <ConfigSelector
              category="email"
              selectedConfig={selectedConfigs.smtpHost}
              onConfigSelect={(config) => handleConfigChange('smtpHost', config)}
              label="Servidor SMTP"
              description="Endereço do servidor de email"
            />
            <ConfigSelector
              category="email"
              selectedConfig={selectedConfigs.smtpPort}
              onConfigSelect={(config) => handleConfigChange('smtpPort', config)}
              label="Porta SMTP"
              description="Porta do servidor de email (ex: 587, 465, 25)"
            />
            <ConfigSelector
              category="email"
              selectedConfig={selectedConfigs.smtpUser}
              onConfigSelect={(config) => handleConfigChange('smtpUser', config)}
              label="Usuário SMTP"
              description="Usuário para autenticação SMTP"
            />
            <ConfigSelector
              category="email"
              selectedConfig={selectedConfigs.smtpPassword}
              onConfigSelect={(config) => handleConfigChange('smtpPassword', config)}
              label="Senha SMTP"
              description="Senha para autenticação SMTP"
            />
          </div>
        </div>
      </div>

      {/* Configurações de Banco de Dados */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <ServerIcon className="h-6 w-6 text-green-600 mr-3" />
            <h3 className="text-lg font-medium text-gray-900">Banco de Dados PostgreSQL</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ConfigSelector
              category="database"
              selectedConfig={selectedConfigs.pgHost}
              onConfigSelect={(config) => handleConfigChange('pgHost', config)}
              label="Host do PostgreSQL"
              description="Endereço do servidor PostgreSQL"
              required
            />
            <ConfigSelector
              category="database"
              selectedConfig={selectedConfigs.pgPort}
              onConfigSelect={(config) => handleConfigChange('pgPort', config)}
              label="Porta do PostgreSQL"
              description="Porta do servidor PostgreSQL (padrão: 5432)"
            />
            <ConfigSelector
              category="database"
              selectedConfig={selectedConfigs.pgUser}
              onConfigSelect={(config) => handleConfigChange('pgUser', config)}
              label="Usuário PostgreSQL"
              description="Usuário para conectar ao PostgreSQL"
              required
            />
            <ConfigSelector
              category="database"
              selectedConfig={selectedConfigs.pgPassword}
              onConfigSelect={(config) => handleConfigChange('pgPassword', config)}
              label="Senha PostgreSQL"
              description="Senha do usuário PostgreSQL"
              required
            />
          </div>
        </div>
      </div>

      {/* Resumo das configurações */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Resumo da Configuração</h4>
        <div className="space-y-2">
          {Object.entries(selectedConfigs).map(([field, config]) => {
            if (!config) return null;
            return (
              <div key={field} className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{field}:</span>
                <div className="flex items-center">
                  {config.type === 'global' ? (
                    <div className="flex items-center text-green-600">
                      <CheckIcon className="h-4 w-4 mr-1" />
                      <span>global_config.{config.key}</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-blue-600">
                      <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                      <span>Valor personalizado</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ModularBackupConfig;